import PracticePage from "@/components/PracticePage";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";
import { useSEO } from "@/lib/seo";

export default function EstatePlanning() {
    useSEO({
        title: "Estate Planning Attorneys | Wills, Trusts & Succession",
        description:
            "Estate planning, wills, trusts, powers of attorney, and succession plans for families and closely-held businesses.",
        path: "/estate-planning",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Estate Planning",
            provider: { "@type": "LegalService", name: "Chesterton" }
        }
    });

    return (
        <>
            <PracticePage
                slug="estate-planning"
                kicker="Practice · Estate Planning"
                title={<>Plan now, so what you built doesn’t become an argument later.</>}
                lede="Most estates aren’t complicated until something goes wrong. We build wills, trusts, and succession plans that hold up under the weight of real life: illness, partnerships, blended families, and assets that have been in the family longer than most marriages."
                heroImg="https://images.unsplash.com/photo-1480561807109-e2aa33f23be8?w=2000&q=80&auto=format&fit=crop"
                bullets={[
                    "Last wills and testaments",
                    "Revocable and irrevocable trusts",
                    "Durable and healthcare powers of attorney",
                    "Advance directives and HIPAA authorizations",
                    "Farm and small-business succession planning",
                    "Beneficiary designations and asset titling",
                    "Special-needs and minor-child planning",
                    "Probate and trust administration"
                ]}
                sections={[
                    {
                        h: "Wills you can actually live with.",
                        p: "A good will is short, current, and consistent with how your property is titled. We sit down with you to understand who matters to you, what you own, and what you want to happen. Then we draft something that reads like a person wrote it, not a form."
                    },
                    {
                        h: "Trusts when they make sense, not by default.",
                        p: "Trusts are powerful in the right hands and a needless complication in the wrong ones. We will tell you honestly whether a revocable living trust adds value for your situation, or whether a tidy will and clean titling is the better tool."
                    },
                    {
                        h: "Succession for farm and family business.",
                        p: "When the asset that matters most is the land or the operation itself, the plan must keep the business workable for the heir who runs it and fair to the heirs who don’t. We build buy-sell terms, lease structures, and entity layers that prevent forced sales at the wrong moment."
                    },
                    {
                        h: "Probate and administration when the time comes.",
                        p: "If you’re the executor or trustee, we will walk you through inventory, notice, claims, tax filings, and final distribution: the non-contested administration of the estate through the appropriate Tennessee probate process."
                    }
                ]}
            />

            <section className="py-20 lg:py-28 bg-white border-t border-acre-line" data-testid="will-based-section">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 scroll-mt-24" id="will-based-plan">
                    <div className="lg:col-span-5">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">Plan Type</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            <a href="#will-based-plan" className="hover:opacity-80 transition-opacity">Will-based plan</a>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-acre-muted text-base lg:text-lg leading-relaxed">
                        <p>
                            The straightforward plan. Everything passes at death through your will, and probate, usually uncomplicated when the plan is drafted well, settles it. A will-based plan typically includes, <strong>for each spouse</strong>:
                        </p>
                        <ul className="space-y-2.5 list-disc pl-6 marker:text-acre-secondary text-acre-ink">
                            <li>A last will and testament, with a guardian designation for minor children (and a minor’s trust inside the will where it makes sense)</li>
                            <li>A durable financial power of attorney</li>
                            <li>An advance care plan (healthcare power of attorney and living will)</li>
                            <li>A HIPAA waiver</li>
                            <li>A remains designation</li>
                            <li>A tangible personal property memorandum</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-acre-bg border-t border-acre-line" data-testid="trust-based-section">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 scroll-mt-24" id="trust-based-plan">
                    <div className="lg:col-span-5">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">Plan Type</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            <a href="#trust-based-plan" className="hover:opacity-80 transition-opacity">Trust-based plan</a>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-acre-muted text-base lg:text-lg leading-relaxed">
                        <p>
                            The more comprehensive plan. A revocable living trust holds title to your assets during your life and distributes them after death without going through probate. This is useful for families with real estate in more than one state, closely-held business interests, or a strong preference for privacy. A trust-based plan typically includes, <strong>for each spouse</strong>:
                        </p>
                        <ul className="space-y-2.5 list-disc pl-6 marker:text-acre-secondary text-acre-ink">
                            <li>A revocable living trust</li>
                            <li>A pour-over will</li>
                            <li>A certificate of trust</li>
                            <li>Funding instruments to move assets into the trust (deeds, assignments, beneficiary re-designations)</li>
                            <li>A durable financial power of attorney</li>
                            <li>An advance care plan (healthcare power of attorney and living will)</li>
                            <li>A HIPAA waiver</li>
                            <li>A remains designation</li>
                            <li>A tangible personal property memorandum</li>
                        </ul>
                    </div>
                </div>
            </section>

            <FAQ />
            <CTABanner />
        </>
    );
}
